import { cloudFunctions } from '@/firebaseInit';
import type {
  AnswerOwnBookingOfferRequestType,
  AnswerOwnBookingOfferResponseType,
  BookingOfferPreviewType,
  BookingRequestDraftType,
  CancelOwnBookingRequestType,
  CancelOwnBookingResponseType,
  CreateBookingRequestResponseType,
  RespondToBookingOfferRequestType,
  RespondToBookingOfferResponseType,
  SetBookingReferenceRequestType,
  SetBookingRequestStatusRequestType,
  SetBookingRequestStatusResponseType,
} from '@/types/types_bookingRequest';
import type {
  CreateInviteRequestType,
  CreateInviteResponseType,
  InvitePreviewType,
} from '@/types/types_invite';
import type {
  GenerateInvoiceDraftRequestType,
  GenerateInvoiceDraftResponseType,
  IssueInvoiceRequestType,
  IssueInvoiceResponseType,
  RegenerateInvoicePdfRequestType,
  RegenerateInvoicePdfResponseType,
  VoidInvoiceRequestType,
} from '@/types/types_invoice';
import type {
  DeletePartnerUserRequestType,
  SetPartnerUserRoleRequestType,
  SetPartnerUserStatusRequestType,
} from '@/types/types_partnerUser';
import type {
  GuideAvailabilityRequestType,
  GuideAvailabilityResponseType,
  PortalPingResponseType,
} from '@/types/types_portal';
import { httpsCallable } from 'firebase/functions';

// Typed wrappers around the Cloud Functions in `functions/`. Request and
// response shapes come from the shared types, which the functions import too —
// mirrors the /portal convention (src/util/callable_functions.ts there).

/**
 * The human-readable half of a callable error.
 *
 * Every `HttpsError` thrown in `functions/` carries a message written for the
 * person who will read it — "Aman Tokyo already has a manager", not a
 * status code — so showing it verbatim is the point, not a fallback.
 */
export function callableErrorMessage(e: unknown): string {
  const message = (e as { message?: string }).message;
  return message || 'Something went wrong. Please try again.';
}

/**
 * Issues a single-use invitation and either emails it (`delivery: 'email'`, the
 * default) or returns the link for the admin to send on (`delivery: 'link'`).
 *
 * A returned URL is the only time the plaintext token exists outside the server
 * — show it to the admin immediately; it cannot be retrieved again. Check
 * `delivered` rather than assuming: an email that SendGrid refused comes back as
 * a link with `sendError` set.
 */
export const createPartnerInvite = httpsCallable<CreateInviteRequestType, CreateInviteResponseType>(
  cloudFunctions,
  'createPartnerInvite',
);

export const revokePartnerInvite = httpsCallable<{ inviteId: string }, void>(
  cloudFunctions,
  'revokePartnerInvite',
);

/** Describes an invitation before sign-in. Callable while signed out. */
export const invitePreview = httpsCallable<{ inviteId: string; token: string }, InvitePreviewType>(
  cloudFunctions,
  'invitePreview',
);

export const redeemPartnerInvite = httpsCallable<{ inviteId: string; token: string }, { partnerId: string }>(
  cloudFunctions,
  'redeemPartnerInvite',
);

// --- Partner account management ---
//
// `partnerUsers` is closed to client writes, so these are the only way to
// change an account after it exists. Each one re-checks who is calling; the UI
// hides what a given user can't do, but the server is what decides.

/** Suspends or restores an account. Reversible — the document stays. */
export const setPartnerUserStatus = httpsCallable<SetPartnerUserStatusRequestType, void>(
  cloudFunctions,
  'setPartnerUserStatus',
);

/** Promotes a staff account to partner manager, or demotes one. Eighty Days only. */
export const setPartnerUserRole = httpsCallable<SetPartnerUserRoleRequestType, void>(
  cloudFunctions,
  'setPartnerUserRole',
);

/** Removes an account, freeing the partner's manager role if it held it. */
export const deletePartnerUser = httpsCallable<DeletePartnerUserRequestType, void>(
  cloudFunctions,
  'deletePartnerUser',
);

// --- Booking requests ---

/**
 * Submits a partner's booking request and notifies the Eighty Days product desk.
 *
 * The partner it is attributed to comes from the caller's own account, not from
 * this payload. `notified: false` in the response means the request was stored
 * but the email did not go out — worth telling the concierge, since it makes a
 * phone call the safer follow-up.
 */
export const createBookingRequest = httpsCallable<BookingRequestDraftType, CreateBookingRequestResponseType>(
  cloudFunctions,
  'createBookingRequest',
);

/**
 * Moves a request to its next state. Eighty Days admins only.
 *
 * The payload is discriminated by the status being moved to, so a hold arrives
 * with a date, a price and a deadline, and a cancellation with an initiator and
 * a reason — there is no shape that expresses one without the other.
 *
 * Rejections here are ordinary: `failed-precondition` means somebody else moved
 * the request first, which on a queue two people are working is a thing to
 * show, not an error to log. The message says what it is now.
 */
export const setBookingRequestStatus = httpsCallable<
  SetBookingRequestStatusRequestType,
  SetBookingRequestStatusResponseType
>(cloudFunctions, 'setBookingRequestStatus');

/**
 * Records /portal's code for a booking, or clears one entered against the wrong
 * booking. Eighty Days admins only.
 *
 * Not a transition, which is why it is not `setBookingRequestStatus`: the
 * reference can be set at any point in a request's life, ended ones included,
 * and nothing about the workflow turns on it. A `failed-precondition` means the
 * code is already on another booking, and the message says which.
 */
export const setBookingReference = httpsCallable<SetBookingReferenceRequestType, void>(
  cloudFunctions,
  'setBookingReference',
);

/**
 * Cancels one of the caller's own confirmed bookings. Partner accounts.
 *
 * Deliberately not `setBookingRequestStatus` with a different payload: a partner does
 * not get to say why its booking ended or what that costs, because the cheapest
 * reason is the one it would always pick. `acceptedFee` is the figure the screen
 * showed, and the server refuses anything short of what is due — a
 * `failed-precondition` here usually means the free window closed while the page
 * was open, and its message carries the real amount to show and ask again.
 */
export const cancelOwnBooking = httpsCallable<CancelOwnBookingRequestType, CancelOwnBookingResponseType>(
  cloudFunctions,
  'cancelOwnBooking',
);

// --- Booking offers ---
//
// The emailed offer link, `/booking/:requestId?t=<token>`. Both callables run
// unauthenticated — the token is the credential — so neither needs the concierge
// to be signed in, which is the point: offers get answered from phones.

/** Describes a held date to whoever holds the link. Callable while signed out. */
export const bookingOfferPreview = httpsCallable<
  { requestId: string; token: string },
  BookingOfferPreviewType
>(cloudFunctions, 'bookingOfferPreview');

/**
 * Records the answer. The token is re-checked inside the write, so a page left
 * open past the deadline is refused rather than honoured — `result` comes back
 * as 'expired' or 'already-answered' instead of throwing.
 */
export const respondToBookingOffer = httpsCallable<
  RespondToBookingOfferRequestType,
  RespondToBookingOfferResponseType
>(cloudFunctions, 'respondToBookingOffer');

/**
 * The same answer from the portal, for a signed-in account at the partner the
 * request belongs to. No token: the account is the credential.
 *
 * Throws where the link path returns a result, and the difference is the caller
 * rather than the rule. A stranger on a link needs a page explaining that their
 * offer lapsed; a concierge whose list just went stale needs the message and
 * their list refreshed, which is what a `failed-precondition` gets them.
 */
export const answerOwnBookingOffer = httpsCallable<
  AnswerOwnBookingOfferRequestType,
  AnswerOwnBookingOfferResponseType
>(cloudFunctions, 'answerOwnBookingOffer');

// --- Invoices ---

/**
 * Works out what a partner owes for a month and writes it down as a draft.
 *
 * Safe to call as often as you like: the document id is derived from the partner
 * and the month, nothing is stamped, and no number is allocated. Pressing it
 * twice produces the same draft rather than a second invoice.
 *
 * Refused once the invoice has been issued — at that point the partner is holding
 * it, and anything owing since belongs on the next one.
 */
export const generateInvoiceDraft = httpsCallable<
  GenerateInvoiceDraftRequestType,
  GenerateInvoiceDraftResponseType
>(cloudFunctions, 'generateInvoiceDraft');

/**
 * Turns a draft into the invoice a partner is sent: stamps every booking it bills,
 * allocates the number, and renders the PDF.
 *
 * The one irreversible step, and the figures stop being editable at it. Check
 * `pdfRendered` rather than assuming — a render that failed leaves a real,
 * issued invoice whose document has to be produced again with
 * `regenerateInvoicePdf`, which is a different problem from an invoice that did
 * not go out.
 */
export const issueInvoice = httpsCallable<IssueInvoiceRequestType, IssueInvoiceResponseType>(
  cloudFunctions,
  'issueInvoice',
);

/**
 * Takes back an invoice issued by mistake, releasing its bookings onto the next
 * draft.
 *
 * Only for one that has not been sent. Once the partner has the document the money
 * has been asked for, and the fix is a credit on the following invoice rather
 * than a retraction of something they can still read — nothing here can tell the
 * two apart, so the screen has to ask.
 */
export const voidInvoice = httpsCallable<VoidInvoiceRequestType, void>(
  cloudFunctions,
  'voidInvoice',
);

/**
 * Produces the document for an invoice already issued, when the figures are
 * right and only the PDF is wrong or missing.
 *
 * The way back from `issueInvoice` answering `pdfRendered: false`, and the right
 * button for a partner who has lost the file or an invoice carrying bank details
 * since corrected. Bills nothing, stamps nothing, and moves no money — which is
 * what makes it the alternative to voiding rather than a milder version of it.
 */
export const regenerateInvoicePdf = httpsCallable<
  RegenerateInvoicePdfRequestType,
  RegenerateInvoicePdfResponseType
>(cloudFunctions, 'regenerateInvoicePdf');

// --- Portal ---

/**
 * Checks that this project can authenticate against /portal's internal API.
 *
 * A diagnostic, and the only way to exercise that path: the identity comes from
 * the deployed function's own service account, so it cannot be reproduced from a
 * browser or a laptop. Eighty Days admins only, and it touches no data on either
 * side. Failures come back as the message from Cloud Run, which is what
 * distinguishes "not granted" from "token rejected".
 */
export const pingPortal = httpsCallable<void, PortalPingResponseType>(
  cloudFunctions,
  'pingPortal',
);

/**
 * Which days the named tour leaders are free.
 *
 * A callable rather than a Firestore read because the answer is /portal's, not
 * ours: guides' tours and days off live over there, and nothing in this
 * project's database knows about them. Signed-in callers only — a guide's diary
 * is staff data.
 *
 * Whole days, and a guide /portal does not recognise comes back absent rather
 * than empty — the two mean different things to a concierge, so keep them
 * distinct in the UI. Rejections are ordinary here: /portal being unreachable is
 * a calendar that cannot be drawn, not a page that cannot be shown.
 */
export const guideAvailability = httpsCallable<GuideAvailabilityRequestType, GuideAvailabilityResponseType>(
  cloudFunctions,
  'guideAvailability',
);
